import { useCallback } from 'react';

import { CommonApiProps, useLazyGetProfileQuery } from '@gif/shared-apis';
import { User, UserState } from '@gif/shared-global';

import { setUser as mutateUser, resetUserIsLoading } from '../slices';

interface UseUserArgs {
  useAppSelector: any;
  useAppDispatch: any;
  getUserProfileApiConfig?: CommonApiProps;
}

interface UseUser {
  loadUser: () => Promise<void>;
  user: User;
  setUser: (user: User) => void;
  isLoading: boolean;
}
const loggedOutUser = {
  loggedIn: false,
};

const getUser = async () => {
  let user: User;
  try {
    user = await window.mlbOkta.isLoggedIn();
  } catch (error) {
    // TODO: Handle error with toast notification (or some other non-intrusive way)
    user = loggedOutUser;
  }
  return user;
};

export const useUser = (props: UseUserArgs): UseUser => {
  const { useAppSelector, useAppDispatch, getUserProfileApiConfig } = props;
  const data = useAppSelector((state: UserState) => state.user);
  const user = data.user;
  const [getUserProfile] = useLazyGetProfileQuery();
  const dispatch = useAppDispatch();
  const setUser = useCallback(
    (user: User) => {
      dispatch(mutateUser(user));
    },
    [dispatch]
  );
  const loadUser = useCallback(async (): Promise<any> => {
    resetUserIsLoading();
    const user = await getUser();
    const userAccessToken = user.accessToken?.accessToken;
    if (!getUserProfileApiConfig || !user.loggedIn || !userAccessToken) {
      setUser(user);
      return user;
    }
    // Unfortunately, the data from `window.mlbOkta.isLoggedIn()` does not have all of the profile information we need and it's cached
    const userProfile = await getUserProfile({
      accessToken: userAccessToken,
      baseUrl: getUserProfileApiConfig.baseUrl,
      headers: getUserProfileApiConfig.headers,
    });
    const {
      birthDay,
      birthMonth,
      birthYear,
      fanPlatformOptedIn,
      residencyCountry,
      residencyStateProvince,
      email,
      firstName,
      lastName,
    } = userProfile.data || {};
    const userWithProfile = {
      ...user,
      accessToken: {
        ...user.accessToken,
        claims: {
          ...user.accessToken?.claims,
          sub: email,
        },
      },
      idToken: {
        ...user.idToken,
        claims: {
          ...user.idToken?.claims,
          birthDay,
          birthMonth,
          birthYear,
          fanPlatformOptedIn,
          residencyCountry,
          residencyStateProvince,
          email,
          firstName,
          lastName,
        },
      },
    };
    setUser(userWithProfile);
    return userWithProfile;
  }, [getUserProfileApiConfig, getUserProfile, setUser]);
  return {
    loadUser,
    user,
    setUser,
    isLoading: data.isLoading,
  };
};

export default useUser;
